
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Trash2, RefreshCw, Calendar, CreditCard, Shield, Users } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';

import { merchantApi } from '../lib/api/merchantApi';
import { MerchantProfile } from '../lib/api/types';

export const MerchantProfileList: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Query to fetch all merchant profiles
  const {
    data: profilesData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['merchant-profiles'],
    queryFn: merchantApi.getAllMerchantProfiles,
  });

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 animate-spin" />
            Loading Merchant Profiles...
          </CardTitle>
        </CardHeader>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-destructive">Error Loading Profiles</CardTitle>
          <CardDescription>
            {error instanceof Error ? error.message : 'An unknown error occurred'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => refetch()} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const profiles = profilesData?.merchantProfiles || [];

  return (
    <div className="space-y-6">
      {/* Header with actions */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Merchant Profiles</h2>
          <p className="text-muted-foreground">
            {profiles.length === 0
              ? 'No merchant profiles found'
              : `${profiles.length} profile${profiles.length === 1 ? '' : 's'}`
            }
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => refetch()} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Profile list */}
      {profiles.length === 0 ? (
        <Card className="w-full">
          <CardContent className="py-8">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 mb-4 rounded-full bg-muted flex items-center justify-center">
                <Shield className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No Merchant Profiles</h3>
              <p className="text-muted-foreground">
                No merchant profiles are currently available.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {profiles.map((profile: MerchantProfile) => (
            <MerchantProfileCard
              key={profile.profileId}
              profile={profile}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface MerchantProfileCardProps {
  profile: MerchantProfile;
}

const MerchantProfileCard: React.FC<MerchantProfileCardProps> = ({ profile }) => {
  const queryClient = useQueryClient();

  const deleteProfileMutation = useMutation({
    mutationFn: (profileId: number) => merchantApi.deleteMerchantProfile(profileId),
    onSuccess: (response) => {
      toast.success('Merchant profile deleted successfully', {
        description: `Profile ID: ${response.profileId}`,
      });
      queryClient.invalidateQueries({ queryKey: ['merchant-profiles'] });
    },
    onError: (error: any) => {
      console.error('Error deleting merchant profile:', error);
      const errorMessage = error.response?.data?.ErrorMessage || error.message || 'Failed to delete merchant profile';
      toast.error('Failed to delete merchant profile', {
        description: errorMessage,
      });
    },
  });

  const handleDelete = () => {
    deleteProfileMutation.mutate(profile.profileId);
  };

  const formatProcessorData = (processorData: { ProcessorField: string; Value: string }[]) => {
    return processorData.map((field, index) => (
      <div key={index} className="flex justify-between text-sm">
        <span className="text-muted-foreground font-medium">{field.ProcessorField}:</span>
        <span className="font-mono text-xs bg-muted px-2 py-1 rounded max-w-48 truncate" title={field.Value}>
          {field.Value}
        </span>
      </div>
    ));
  };

  // Find account number from processor data
  const accountNumberField = profile.processorData.find(
    field => field.ProcessorField === 'accountNum'
  );
  const accountNumber = accountNumberField?.Value || 'N/A';

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="h-5 w-5" />
              {profile.profileName}
            </CardTitle>
            <CardDescription>
              Profile ID: {profile.profileId} • {profile.paymentProcessor}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <div className={`px-3 py-1 rounded-full text-xs font-medium ${
              profile.status === 'Active'
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-800'
            }`}>
              {profile.status}
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={deleteProfileMutation.isPending}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Merchant Profile</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete profile "{profile.profileName}"?
                    This action cannot be undone and will remove all associated payment processor configuration.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {deleteProfileMutation.isPending ? 'Deleting...' : 'Delete'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Basic Info */}
          <div className="grid gap-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground flex items-center gap-1">
                <CreditCard className="h-3 w-3" />
                Account Number:
              </span>
              <span className="font-mono text-xs bg-muted px-2 py-1 rounded">
                {accountNumber}
              </span>
            </div>
            {profile.partnerName && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  Partner:
                </span>
                <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                  {profile.partnerName}
                </span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Payment Processor:</span>
              <span className="font-medium">{profile.paymentProcessor}</span>
            </div>
          </div>

          {/* Processor Data */}
          {profile.processorData.length > 0 && (
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Processor Configuration
              </h4>
              <div className="space-y-2">
                {formatProcessorData(profile.processorData)}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
